/**
 * Kennwerte aus dem Trainingsprotokoll fuer automatische Key Results
 * (F-34 D) - rein und getestet. Sportziele sind normale Ziele (V-06), ihr
 * Ist-Wert kommt von hier statt von Hand.
 */
import { kraftVerlauf, laeufe, umfang } from '@/domain/training/auswertung';
import type { TrainingsProtokoll, Uebung } from '@/domain/training/typen';
import type { KeyResultDirection, LocalDate, TrainingsMass } from '@/domain/types';
import { endOfWeek, startOfWeek } from '@/lib/datetime';

export const MASS_ARTEN: Record<TrainingsMass['art'], { label: string; einheit: string; richtung: KeyResultDirection; uebung?: 'kraft' | 'skill' }> = {
  lauf_zeit: { label: 'Beste Laufzeit', einheit: 'min', richtung: 'senken' },
  wdh_max: { label: 'Meiste Wiederholungen am Stück', einheit: 'Wdh', richtung: 'steigern', uebung: 'kraft' },
  e1rm: { label: 'Maximalgewicht (1RM geschätzt)', einheit: 'kg', richtung: 'steigern', uebung: 'kraft' },
  km_woche: { label: 'Laufkilometer je Woche', einheit: 'km', richtung: 'steigern' },
  einheiten_woche: { label: 'Einheiten je Woche', einheit: 'Einheiten', richtung: 'steigern' },
  skill_stufe: { label: 'Skill-Stufe', einheit: 'Stufe', richtung: 'steigern', uebung: 'skill' },
};

const eineStelle = (x: number) => Math.round(x * 10) / 10;

/**
 * Der Kennwert im Zeitraum `von`-`bis` (Bestwerte) bzw. in der Woche von
 * `heute` (Wochenwerte). `null` = noch kein Wert, dann bleibt der alte stehen.
 */
export function trainingsWert(
  mass: TrainingsMass, protokolle: TrainingsProtokoll[], bibliothek: ReadonlyMap<string, Uebung>,
  zeitraum: { von: LocalDate; bis: LocalDate }, heute: LocalDate,
): number | null {
  const imZeitraum = protokolle.filter((p) => p.datum >= zeitraum.von && p.datum <= zeitraum.bis && p.datum <= heute);
  switch (mass.art) {
    case 'lauf_zeit': {
      if (!(mass.km > 0)) return null;
      const passend = laeufe(imZeitraum).filter((l) => l.km >= mass.km);
      if (!passend.length) return null;
      return eineStelle(Math.min(...passend.map((l) => (l.pace * mass.km) / 60)));
    }
    case 'wdh_max': {
      const p = kraftVerlauf(imZeitraum, mass.uebungId);
      return p.length ? Math.max(...p.map((x) => x.wdh)) : null;
    }
    case 'e1rm': {
      const p = kraftVerlauf(imZeitraum, mass.uebungId).filter((x) => x.e1rm > 0);
      return p.length ? Math.max(...p.map((x) => x.e1rm)) : null;
    }
    case 'km_woche':
      return umfang(protokolle, startOfWeek(heute), endOfWeek(heute)).km;
    case 'einheiten_woche':
      return umfang(protokolle, startOfWeek(heute), endOfWeek(heute)).einheiten;
    case 'skill_stufe': {
      const stufen = bibliothek.get(mass.uebungId)?.stufen ?? [];
      let best = 0;
      for (const p of imZeitraum) {
        if (p.status !== 'fertig') continue;
        for (const pu of p.uebungen) {
          if (pu.uebungId !== mass.uebungId || pu.uebersprungen || !pu.stufe || !pu.saetze.some((s) => s.erledigt)) continue;
          best = Math.max(best, stufen.indexOf(pu.stufe) + 1);
        }
      }
      return best || null;
    }
  }
}

/** „Beste Laufzeit 10 km“, „Meiste Wiederholungen am Stück: Klimmzug“ … */
export function massText(mass: TrainingsMass, bibliothek: ReadonlyMap<string, Uebung>): string {
  const basis = MASS_ARTEN[mass.art].label;
  if (mass.art === 'lauf_zeit') return `${basis} ${mass.km.toLocaleString('de-DE')} km`;
  if ('uebungId' in mass) return `${basis}: ${bibliothek.get(mass.uebungId)?.name ?? 'Übung gelöscht'}`;
  return basis;
}

